import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export function usePagination() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || "1");
  const pageSize = Number(searchParams.get("pageSize") || "50");
  const sort = searchParams.get("sort") || "";
  const search = searchParams.get("search") || "";

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter[")) {
        f[key] = value;
      }
    });
    return f;
  }, [searchParams]);

  const setPage = useCallback(
    (p: number) => {
      setSearchParams((prev) => {
        prev.set("page", String(p));
        return prev;
      });
    },
    [setSearchParams]
  );

  const setSort = useCallback(
    (s: string) => {
      setSearchParams((prev) => {
        if (s) {
          prev.set("sort", s);
        } else {
          prev.delete("sort");
        }
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const setSearch = useCallback(
    (s: string) => {
      setSearchParams((prev) => {
        if (s) {
          prev.set("search", s);
        } else {
          prev.delete("search");
        }
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const setPageSize = useCallback(
    (size: number) => {
      setSearchParams((prev) => {
        prev.set("pageSize", String(size));
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const setFilter = useCallback(
    (field: string, value: string | null) => {
      setSearchParams((prev) => {
        const key = `filter[${field}]`;
        if (value) {
          prev.set(key, value);
        } else {
          prev.delete(key);
        }
        prev.set("page", "1");
        return prev;
      });
    },
    [setSearchParams]
  );

  const params = useMemo(() => {
    const p: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
    if (sort) p.sort = sort;
    if (search) p.search = search;
    Object.entries(filters).forEach(([k, v]) => {
      p[k] = v;
    });
    return p;
  }, [page, pageSize, sort, search, filters]);

  return { page, pageSize, sort, search, filters, params, setPage, setPageSize, setSort, setSearch, setFilter };
}
